// React Components
import React from "react";

// Material UI Components
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Select } from '@mui/material';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';


// Function to insert a default start and end date selector (defaults to startDate - yesterday, and endDate - today).
export default function StartAndEndDateSelector(props){

    const { selectedStartDate, setSelectedStartDate, selectedStartTime, setSelectedStartTime, selectedEndDate, setSelectedEndDate, selectedEndTime, setSelectedEndTime, responsive = false, useUtc = true, setUseUtc, showTimeZoneToggle = false, matchingControlSize = false, stackLabelsOnMobile = matchingControlSize } = props;
    const matchingControlSx = matchingControlSize ? { width: { xs: '100%', md: 220 } } : undefined;
    const dateTimeRowSx = stackLabelsOnMobile ? {
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '140px 220px 65px 220px' },
        rowGap: 1,
        alignItems: 'center',
        width: '100%'
    } : {
        display: 'flex',
        flexWrap: matchingControlSize ? 'wrap' : 'nowrap',
        rowGap: matchingControlSize ? '8px' : 0
    };
    const dateLabelSx = stackLabelsOnMobile
        ? { width: { md: 140 }, display: 'flex', alignItems: 'center' }
        : { width: '140px', display: 'flex', alignItems: 'center' };
    const timeLabelSx = stackLabelsOnMobile
        ? { width: { md: 65 }, display: 'flex', alignItems: 'center', pl: { xs: 0, md: 2 } }
        : { width: '65px', display: 'flex', alignItems: 'center', pl: 2, boxSizing: 'border-box' };

    // Create an array of times in 30-minute increments.
    const generateTimes = () => {
        const times = [];
        for (let hours = 0; hours < 24; hours++) {
            for (let minutes = 0; minutes < 60; minutes += 30) {
                const formattedHour = hours.toString().padStart(2, '0');
                const formattedMinute = minutes.toString().padStart(2, '0');
                times.push(`${formattedHour}:${formattedMinute}`);
            }
        }
        return times;
    };

    const times = generateTimes();

    // Event handler for when the date picker control changes value.
    const handleDateChange = (date, setSelectedDate) => {
        // Use dayjs to format the date as needed
        const formattedDate = dayjs(date).format('YYYY-MM-DD'); // Adjust the format as needed
        setSelectedDate(formattedDate);
    };

    if (responsive) {
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8} md={4}>
                        <DatePicker
                            label={`Start date (${useUtc ? 'UTC' : 'Local'})`}
                            value={dayjs(selectedStartDate)}
                            onChange={date => handleDateChange(date, setSelectedStartDate)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField select fullWidth size="small" label="Start time" value={selectedStartTime} onChange={event => setSelectedStartTime(event.target.value)}>
                            {times.map(time => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8} md={4}>
                        <DatePicker
                            label={`End date (${useUtc ? 'UTC' : 'Local'})`}
                            value={dayjs(selectedEndDate)}
                            onChange={date => handleDateChange(date, setSelectedEndDate)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField select fullWidth size="small" label="End time" value={selectedEndTime} onChange={event => setSelectedEndTime(event.target.value)}>
                            {times.map(time => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                        </TextField>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        );
    }

    return(
        <>
        <Grid item xs={12}>
            {/* Start Date input field control. */}
            <Box sx={dateTimeRowSx}>
                <Box sx={dateLabelSx}>
                    <b>Start Date ({useUtc ? 'UTC' : 'Local'}):</b>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                     <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            name="startDate"                            
                            value={dayjs(selectedStartDate)}                            
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            onChange={ (date) => handleDateChange(date, setSelectedStartDate) }                                           
                            slotProps={matchingControlSize ? { textField: { size: 'small', sx: matchingControlSx } } : undefined}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={timeLabelSx}>
                    <b>Time:</b>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Select
                        name="startTime"
                        value={selectedStartTime}
                        onChange={(event) => setSelectedStartTime(event.target.value)}                         
                        size={matchingControlSize ? 'small' : undefined}
                        sx={matchingControlSx}
                    >
                        {times.map((time, index) => (
                        <MenuItem key={index} value={time}>
                            {time}
                        </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            <div style={{ height: '15px' }}></div>

            {/* End Date input field control. */}
            <Box sx={dateTimeRowSx}>
                <Box sx={dateLabelSx}>
                    <b>End Date ({useUtc ? 'UTC' : 'Local'}):</b>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker    
                            name="endDate"                            
                            value={dayjs(selectedEndDate)}                            
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            onChange={ (date) => handleDateChange(date, setSelectedEndDate) }
                            slotProps={matchingControlSize ? { textField: { size: 'small', sx: matchingControlSx } } : undefined}
                        />
                    </LocalizationProvider>                
                </Box>
                <Box sx={timeLabelSx}>
                    <b>Time:</b>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Select
                        name="endTime"
                        value={selectedEndTime}
                        onChange={(event) => setSelectedEndTime(event.target.value)}                         
                        size={matchingControlSize ? 'small' : undefined}
                        sx={matchingControlSx}
                    >
                        {times.map((time, index) => (
                        <MenuItem key={index} value={time}>
                            {time}
                        </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>
            {showTimeZoneToggle && setUseUtc && (
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={<Switch checked={useUtc} onChange={event => setUseUtc(event.target.checked)} />}
                    label={useUtc ? 'UTC time' : 'Local time'}
                />
            )}
        </Grid>
        </>
    );
};
